/**
 * 投稿の AI 解析の観点 (docs/29、REQ-AIP.1)。master のみ編集。
 *
 * - 既定の観点 = 地区の AI 解析設定そのもの。ここでは投稿詳細のタブに出す表示名だけを決める。
 * - 追加の観点 = 表示名・key・解析設定 (M1) の組。投稿の写真が公開された後、既定の観点に続けて自動で解析し、
 *   投稿詳細で切り替えて見られる。地区の AI 解析設定の上書き (出力フォーマットの差し替えなど) は使わない。
 * - 外しても投稿の結果と解析の記録は消えない (同じ key で追加し直せば表示される)。
 * 机上調査の「調査の追加・変更」(StreetSurveySurveysPanel) と同じ作り。
 */
import { Button, Card, useToast } from '@citizen-walk/shared-ui';
import {
  DEFAULT_POST_ANALYSIS_LABEL,
  MAX_ADDITIONAL_POST_ANALYSES,
  isValidPostAnalysisKey,
  listAdditionalPostAnalyses,
  type AiAnalysisSettings,
  type District,
  type PostAnalysisDef,
} from '@citizen-walk/shared-types';
import { collection, doc, getDocs, serverTimestamp, setDoc } from 'firebase/firestore';
import { useEffect, useState } from 'react';
import { db } from '../../lib/firebase';

export interface DistrictPostAnalysesPanelProps {
  district: District;
  uid: string | null;
  canEdit: boolean;
}

interface SettingsOption {
  id: string;
  name: string;
}

function useSettingsOptions(enabled: boolean): SettingsOption[] {
  const [items, setItems] = useState<SettingsOption[]>([]);
  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    getDocs(collection(db, 'aiAnalysisSettings'))
      .then((snap) => {
        if (cancelled) return;
        setItems(
          snap.docs
            .map((d) => ({ ...(d.data() as AiAnalysisSettings), id: d.id }))
            .filter((s) => !s.archivedAt)
            .map((s) => ({ id: s.id, name: s.displayName || '名称なし' }))
            .sort((a, b) => a.name.localeCompare(b.name, 'ja')),
        );
      })
      .catch(() => {
        if (!cancelled) setItems([]);
      });
    return () => {
      cancelled = true;
    };
  }, [enabled]);
  return items;
}

interface Draft {
  /** 変更中の観点の key (新規は null)。 */
  editingKey: string | null;
  label: string;
  key: string;
  settingsId: string;
}

const EMPTY_DRAFT: Draft = { editingKey: null, label: '', key: '', settingsId: '' };
const inputClass = 'mt-0.5 block w-full rounded border border-slate-300 px-2 py-1 text-sm bg-white';

/** 追加の観点の入力の誤り (pure、テスト用に export)。 */
export function postAnalysisDraftErrors(
  draft: Pick<Draft, 'editingKey' | 'label' | 'key' | 'settingsId'>,
  existing: PostAnalysisDef[],
): string[] {
  const keyError =
    draft.editingKey === null
      ? !draft.key
        ? 'key を入力してください。'
        : !isValidPostAnalysisKey(draft.key)
          ? 'key は半角英小文字・数字をハイフンで区切って入力してください（default は使えません）。'
          : existing.some((a) => a.key === draft.key)
            ? '同じ key の観点があります。'
            : null
      : null;
  return [
    ...(draft.editingKey === null && existing.length >= MAX_ADDITIONAL_POST_ANALYSES
      ? [`追加の観点は ${MAX_ADDITIONAL_POST_ANALYSES} つまでです。`]
      : []),
    ...(!draft.label.trim() ? ['表示名を入力してください。'] : []),
    ...(keyError ? [keyError] : []),
    ...(!draft.settingsId ? ['解析設定を選んでください。'] : []),
  ];
}

export function DistrictPostAnalysesPanel({
  district,
  uid,
  canEdit,
}: DistrictPostAnalysesPanelProps) {
  const { toast } = useToast();
  const settingsOptions = useSettingsOptions(true);
  const analyses = listAdditionalPostAnalyses(district.aiAnalysis);
  const savedDefaultLabel = district.aiAnalysis?.defaultAnalysisLabel ?? '';
  /** 既定の観点の表示名の編集中の値 (null = 未編集で保存済みの値を表示)。 */
  const [defaultLabelDraft, setDefaultLabel] = useState<string | null>(null);
  const defaultLabel = defaultLabelDraft ?? savedDefaultLabel;
  const [draft, setDraft] = useState<Draft>(EMPTY_DRAFT);
  const [saving, setSaving] = useState(false);

  const errors = postAnalysisDraftErrors(draft, analyses);
  const settingsName = (id: string | null | undefined) =>
    settingsOptions.find((o) => o.id === id)?.name ?? '読み込み中';

  async function write(aiAnalysis: Record<string, unknown>, message: string) {
    if (!uid) return;
    setSaving(true);
    try {
      await setDoc(
        doc(db, 'districts', district.id),
        { aiAnalysis, updatedAt: serverTimestamp(), updatedBy: uid },
        { merge: true },
      );
      toast('success', message);
      setDraft(EMPTY_DRAFT);
      setDefaultLabel(null);
    } catch (e) {
      toast('error', `保存に失敗しました: ${(e as Error).message}`);
    } finally {
      setSaving(false);
    }
  }

  async function saveDraft() {
    if (errors.length > 0 || saving) return;
    const entry = { label: draft.label.trim(), settingsId: draft.settingsId };
    const next =
      draft.editingKey === null
        ? [...analyses, { key: draft.key, ...entry }]
        : analyses.map((a) => (a.key === draft.editingKey ? { ...a, ...entry } : a));
    await write(
      { postAnalyses: next },
      draft.editingKey === null
        ? `観点「${entry.label}」を追加しました。`
        : `観点「${entry.label}」を保存しました。`,
    );
  }

  async function remove(a: PostAnalysisDef) {
    if (
      !confirm(
        `観点「${a.label}」を外しますか？投稿に保存した結果と解析の記録は消えません（同じ key で追加し直すと表示されます）。`,
      )
    ) {
      return;
    }
    await write(
      { postAnalyses: analyses.filter((x) => x.key !== a.key) },
      `観点「${a.label}」を外しました。`,
    );
  }

  return (
    <Card padding="lg">
      <h3 className="text-base font-bold text-slate-900 mb-1">AI 解析の観点</h3>
      <p className="text-xs text-slate-600 mb-3">
        1 つの投稿を、防災の視点と Enabling City
        の視点のように別の解析設定でも解析し、投稿詳細で切り替えて見られるようにします。
        追加した観点は、投稿の写真が公開された後に既定の観点に続けて自動で解析します（観点の数だけ
        AI の呼び出しが増えます）。地図の色分け・集計・エクスポートは既定の観点の結果を使います。
      </p>
      {!canEdit && (
        <p className="text-xs text-warning-700 mb-2">変更はマスター管理者のみできます。</p>
      )}

      <div className="grid grid-cols-1 gap-2 mb-3">
        <label className="block text-xs text-slate-600 min-w-0">
          既定の観点（地区の AI 解析設定: {settingsName(district.aiAnalysis?.settingsId)}）の表示名
          <div className="flex flex-wrap gap-2 items-center">
            <input
              className={`${inputClass} max-w-xs`}
              value={defaultLabel}
              placeholder={`未設定なら「${DEFAULT_POST_ANALYSIS_LABEL}」`}
              disabled={!canEdit || saving}
              onChange={(e) => setDefaultLabel(e.target.value)}
            />
            <Button
              size="sm"
              variant="secondary"
              disabled={!canEdit || saving || defaultLabel.trim() === savedDefaultLabel.trim()}
              onClick={() =>
                void write(
                  { defaultAnalysisLabel: defaultLabel.trim() },
                  '既定の観点の表示名を保存しました。',
                )
              }
            >
              表示名を保存
            </Button>
          </div>
        </label>
      </div>

      {analyses.length > 0 ? (
        <ul className="grid grid-cols-1 gap-1 text-sm mb-3">
          {analyses.map((a) => (
            <li key={a.key} className="flex flex-wrap items-center gap-2 min-w-0">
              <span className="font-medium text-slate-800">{a.label}</span>
              <span className="text-xs text-slate-500">解析設定: {settingsName(a.settingsId)}</span>
              {canEdit && (
                <>
                  <Button
                    size="sm"
                    variant="secondary"
                    disabled={saving}
                    onClick={() =>
                      setDraft({
                        editingKey: a.key,
                        key: a.key,
                        label: a.label,
                        settingsId: a.settingsId,
                      })
                    }
                  >
                    変更
                  </Button>
                  <Button
                    size="sm"
                    variant="danger"
                    disabled={saving}
                    onClick={() => void remove(a)}
                  >
                    外す
                  </Button>
                </>
              )}
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-xs text-slate-500 mb-3">追加の観点はまだありません。</p>
      )}

      {canEdit && (
        <div className="grid grid-cols-1 gap-2 border-t border-slate-200 pt-3">
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
            <label className="block text-xs text-slate-600 min-w-0">
              表示名
              <input
                className={inputClass}
                value={draft.label}
                placeholder="例: Enabling City の視点"
                onChange={(e) => setDraft({ ...draft, label: e.target.value })}
              />
            </label>
            <label className="block text-xs text-slate-600 min-w-0">
              key（結果の保存に使う名前。追加後は変えられません）
              <input
                className={inputClass}
                value={draft.key}
                disabled={draft.editingKey !== null}
                placeholder="例: enabling-city"
                onChange={(e) => setDraft({ ...draft, key: e.target.value.trim() })}
              />
            </label>
            <label className="block text-xs text-slate-600 min-w-0">
              解析設定
              <select
                className={inputClass}
                style={{ maxWidth: '100%' }}
                value={draft.settingsId}
                onChange={(e) => setDraft({ ...draft, settingsId: e.target.value })}
              >
                <option value="">選んでください</option>
                {settingsOptions.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.name}
                  </option>
                ))}
              </select>
            </label>
          </div>
          {errors.length > 0 && (draft.label || draft.key || draft.settingsId) && (
            <ul className="list-disc pl-5 text-xs text-danger-700">
              {errors.map((e) => (
                <li key={e}>{e}</li>
              ))}
            </ul>
          )}
          <div className="flex justify-end gap-2">
            {draft.editingKey !== null && (
              <Button size="sm" variant="secondary" onClick={() => setDraft(EMPTY_DRAFT)}>
                キャンセル
              </Button>
            )}
            <Button
              size="sm"
              onClick={() => void saveDraft()}
              disabled={saving || errors.length > 0}
            >
              {draft.editingKey === null ? '観点を追加' : '保存'}
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
}
